import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import ProjectEstimatesService from "../services/ProjectEstimatesService";
import ProjectService from "../services/ProjectService";
import ProjectStoreService from "../services/ProjectStoreService";
import ProjectLocationService from "../services/ProjectLocationService";
import ProjectItemService from "../services/ProjectItemService";
import ItemService from "../services/ItemsServices";
import Breadcrumb from "../components/Breadcrumb";
import StorageService from "../services/StorageService";
import MessageDialog from "../components/MessageDialog";

export default function AddProjectEstimates() {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      projectId: "",
      status: "Active",
    },
  });

  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);
  const [projectItemsList, setProjectItemsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editRecord, setEditRecord] = useState(null);

  const [estimateItems, setEstimateItems] = useState([
    {
      storeId: "",
      locationId: "",
      itemCategoryId: "",
      itemId: "",
      UOM: "",
      estimatedQuantity: "",
      displayOnDashboard: false,
      stockoutCheck: false,
    }
  ]);
  const [locationsMap, setLocationsMap] = useState({});

  const [loading, setLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  const selectedProjectId = watch("projectId");

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  // Load projects dropdown list initially
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const companyId = StorageService.activeCompany().companyId;
        const employeeId = StorageService.loginData()?.employeeId;
        const projRes = await ProjectService.getActiveProjectsByEmployee(companyId, employeeId);
        setProjects(projRes.data || projRes || []);
      } catch (err) {
        console.error("Error loading dropdown data:", err);
        showMessage("Failed to load initial dropdown data.", "error");
      }
    };
    fetchInitialData();
  }, []);

  // Fetch stores and project items when project changes
  useEffect(() => {
    const fetchStoresAndItems = async () => {
      if (!selectedProjectId) {
        if (!id) {
          setStores([]);
          setLocationsMap({});
          setProjectItemsList([]);
          setCategories([]);
          setEstimateItems([
            {
              storeId: "",
              locationId: "",
              itemCategoryId: "",
              itemId: "",
              UOM: "",
              estimatedQuantity: "",
              displayOnDashboard: false,
              stockoutCheck: false,
            }
          ]);
        }
        return;
      }
      try {
        const [storesRes, itemsRes] = await Promise.all([
          ProjectStoreService.getProjectStoreById(selectedProjectId),
          ProjectItemService.getByProjectId(selectedProjectId)
        ]);
        setStores(storesRes.data || storesRes || []);

        const list = itemsRes.data?.data || itemsRes.data || [];
        setProjectItemsList(list);

        // Extract unique categories from project items list
        const uniqueCategories = [];
        const seen = new Set();
        list.forEach(item => {
          if (item.itemCategoryId && !seen.has(item.itemCategoryId)) {
            seen.add(item.itemCategoryId);
            uniqueCategories.push({
              id: item.itemCategoryId,
              name: item.itemCategoryName
            });
          }
        });
        setCategories(uniqueCategories);

        if (!id) {
          setLocationsMap({});
          setEstimateItems([
            {
              storeId: "",
              locationId: "",
              itemCategoryId: "",
              itemId: "",
              UOM: "",
              estimatedQuantity: "",
              displayOnDashboard: false,
              stockoutCheck: false,
            }
          ]);
        }
      } catch (err) {
        console.error("Error loading project stores/items:", err);
        showMessage("Failed to load stores or items for the selected project.", "error");
      }
    };

    if (!editLoading) {
      fetchStoresAndItems();
    }
  }, [id, selectedProjectId, editLoading, setValue]);

  // Handle Modify Mode Load
  useEffect(() => {
    const loadEditDetails = async () => {
      if (!id) return;
      setEditMode(true);
      setEditLoading(true);

      try {
        const res = await ProjectEstimatesService.getById(id);
        const record = res.data?.data || res.data || res;
        if (!record) {
          showMessage("Estimate not found", "error");
          setEditLoading(false);
          return;
        }

        setEditRecord(record);

        // Fetch stores assigned to this project
        const storesRes = await ProjectStoreService.getProjectStoreById(record.projectId);
        setStores(storesRes.data || storesRes || []);

        // Fetch locations assigned to this project & store
        const locationsRes = await ProjectLocationService.getByProjectAndStore(record.projectId, record.storeId);
        setLocationsMap({
          [record.storeId]: locationsRes.data || locationsRes || []
        });

        // Fetch project items list
        const itemsRes = await ProjectItemService.getByProjectId(record.projectId);
        const list = itemsRes.data?.data || itemsRes.data || [];
        setProjectItemsList(list);

        // Extract unique categories from project items list
        const uniqueCategories = [];
        const seen = new Set();
        list.forEach(item => {
          if (item.itemCategoryId && !seen.has(item.itemCategoryId)) {
            seen.add(item.itemCategoryId);
            uniqueCategories.push({
              id: item.itemCategoryId,
              name: item.itemCategoryName
            });
          }
        });
        setCategories(uniqueCategories);

        // Find matched category for edit record
        const matchedItem = list.find(x => String(x.itemId) === String(record.itemId));
        const matchedCategoryId = matchedItem ? String(matchedItem.itemCategoryId) : "";

        // Fill estimateItems
        setEstimateItems([
          {
            storeId: record.storeId || "",
            locationId: record.locationId || "",
            itemCategoryId: matchedCategoryId,
            itemId: record.itemId || "",
            UOM: record.UOM || "",
            estimatedQuantity: record.estimatedQuantity || "",
            displayOnDashboard: record.displayOnDashboard === 1,
            stockoutCheck: record.stockoutCheck === 1
          }
        ]);

        // Pre-fill form values
        setValue("projectId", record.projectId || "");
        setValue("status", record.status || "Active");

      } catch (err) {
        console.error("Error fetching estimate detail:", err);
        showMessage("Error loading estimate details.", "error");
      } finally {
        setEditLoading(false);
      }
    };

    if (projects.length > 0) {
      loadEditDetails();
    }
  }, [id, projects, setValue]);

  const hasDuplicateEstimates = () => {
    const keys = estimateItems
      .map(row => `${row.storeId}_${row.locationId}_${row.itemId}`)
      .filter(k => k.indexOf("undefined") === -1 && k.indexOf("__") === -1 && k !== "____");
    return keys.length !== new Set(keys).size;
  };

  const handleAddRow = () => {
    if (hasDuplicateEstimates()) {
      showMessage("Duplicate store, location, and item combinations are not allowed.", "error");
      return;
    }
    setEstimateItems([
      ...estimateItems,
      {
        storeId: "",
        locationId: "",
        itemCategoryId: "",
        itemId: "",
        UOM: "",
        estimatedQuantity: "",
        displayOnDashboard: false,
        stockoutCheck: false,
      }
    ]);
  };

  const handleDeleteRow = (index) => {
    const updated = [...estimateItems];
    updated.splice(index, 1);
    setEstimateItems(updated);
  };

  const handleStoreChange = async (index, storeId) => {
    const updated = [...estimateItems];
    updated[index].storeId = storeId;
    updated[index].locationId = "";
    updated[index].itemCategoryId = "";
    updated[index].itemId = "";
    updated[index].UOM = "";
    setEstimateItems(updated);

    if (storeId && !locationsMap[storeId]) {
      try {
        const res = await ProjectLocationService.getByProjectAndStore(selectedProjectId, storeId);
        const locList = res.data || res || [];
        setLocationsMap(prev => ({
          ...prev,
          [storeId]: locList
        }));
      } catch (err) {
        console.error("Error loading locations:", err);
      }
    }
  };

  const handleLocationChange = (index, locationId) => {
    const updated = [...estimateItems];
    updated[index].locationId = locationId;
    updated[index].itemCategoryId = "";
    updated[index].itemId = "";
    updated[index].UOM = "";
    setEstimateItems(updated);
  };

  const handleCategoryChange = (index, categoryId) => {
    const updated = [...estimateItems];
    updated[index].itemCategoryId = categoryId;
    updated[index].itemId = "";
    updated[index].UOM = "";
    setEstimateItems(updated);
  };

  const handleItemChange = (index, itemId) => {
    const updated = [...estimateItems];
    updated[index].itemId = itemId;

    const found = projectItemsList.find(x => String(x.itemId) === String(itemId));
    updated[index].UOM = found ? (found.uomName || found.UOM || "") : "";
    setEstimateItems(updated);
  };

  const handleQuantityChange = (index, value) => {
    const updated = [...estimateItems];
    updated[index].estimatedQuantity = value;
    setEstimateItems(updated);
  };

  const handleDashboardChange = (index, checked) => {
    const updated = [...estimateItems];
    updated[index].displayOnDashboard = checked;
    setEstimateItems(updated);
  };

  const handleStockoutCheckChange = (index, checked) => {
    const updated = [...estimateItems];
    updated[index].stockoutCheck = checked;
    setEstimateItems(updated);
  };

  const onSubmit = async (formData) => {
    setLoading(true);

    if (hasDuplicateEstimates()) {
      showMessage("Duplicate store, location, and item combinations are not allowed.", "error");
      setLoading(false);
      return;
    }

    // Validation
    for (let i = 0; i < estimateItems.length; i++) {
      const item = estimateItems[i];
      if (!item.storeId) {
        showMessage(`Store is required for Row ${i + 1}`, "error");
        setLoading(false);
        return;
      }
      if (!item.locationId) {
        showMessage(`Location is required for Row ${i + 1}`, "error");
        setLoading(false);
        return;
      }
      if (!item.itemCategoryId) {
        showMessage(`Category is required for Row ${i + 1}`, "error");
        setLoading(false);
        return;
      }
      if (!item.itemId) {
        showMessage(`Item is required for Row ${i + 1}`, "error");
        setLoading(false);
        return;
      }
      if (!item.estimatedQuantity || Number(item.estimatedQuantity) <= 0) {
        showMessage(`Estimated Quantity must be greater than 0 for Row ${i + 1}`, "error");
        setLoading(false);
        return;
      }
    }

    try {
      const companyId = StorageService.activeCompany().companyId;
      const employeeId = StorageService.loginData().employeeId;

      if (editMode) {
        const item = estimateItems[0];
        const payload = {
          companyId,
          projectId: Number(formData.projectId),
          storeId: Number(item.storeId),
          locationId: Number(item.locationId),
          itemId: Number(item.itemId),
          UOM: item.UOM || null,
          estimatedQuantity: Number(item.estimatedQuantity),
          displayOnDashboard: item.displayOnDashboard ? 1 : 0,
          stockoutCheck: item.stockoutCheck ? 1 : 0,
          status: formData.status || "Active",
          updatedBy: employeeId
        };
        const res = await ProjectEstimatesService.updateProjectEstimate(id, payload);
        const isSuccess =
          res?.status ||
          res?.data?.status ||
          res?.status === 200 ||
          res?.status === 201;

        if (isSuccess) {
          showMessage("Project estimate updated successfully.", "success");
          setTimeout(() => {
            navigate("/cpnl/projectestimates/view");
          }, 1000);
        } else {
          showMessage("Failed to update project estimate.", "error");
        }
      } else {
        // Add Mode: save all rows sequentially
        for (const item of estimateItems) {
          const payload = {
            companyId,
            projectId: Number(formData.projectId),
            storeId: Number(item.storeId),
            locationId: Number(item.locationId),
            itemId: Number(item.itemId),
            UOM: item.UOM || null,
            estimatedQuantity: Number(item.estimatedQuantity),
            displayOnDashboard: item.displayOnDashboard ? 1 : 0,
            stockoutCheck: item.stockoutCheck ? 1 : 0,
            status: formData.status || "Active",
            createdBy: employeeId
          };
          const res = await ProjectEstimatesService.saveProjectEstimate(payload);
          const isSuccess =
            res?.status ||
            res?.data?.status ||
            res?.status === 200 ||
            res?.status === 201;

          if (!isSuccess) {
            throw new Error(`Failed to save estimate for store: ${item.storeId}, item: ${item.itemId}`);
          }
        }

        showMessage("Project estimates saved successfully.", "success");
        setTimeout(() => {
          navigate("/cpnl/projectestimates/view");
        }, 1000);
      }
    } catch (err) {
      console.error("Submit error:", err);
      const backendMsg = err.response?.data?.message || err.message || "Failed to save estimate.";
      showMessage(backendMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/cpnl/projectestimates/view");
  };

  return (
    <>
      <Breadcrumb
        pageTitle={editMode ? "Modify Project Estimate" : "Add Project Estimate"}
        secondTitle="Projects"
      />

      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          <MessageDialog
            message={message}
            type={msgType}
            setMessage={setMessage}
            setMsgType={setMsgType}
          />

          {editLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <div className="mt-2 text-muted">Loading data...</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <input type="hidden" {...register("status")} />

              <div className="row mb-3">
                {/* Project */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Project <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.projectId ? "is-invalid" : ""}`}
                    {...register("projectId", { required: "Project is required" })}
                  >
                    <option value="">Select Project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.projectName} - {p.projectCode}
                      </option>
                    ))}
                  </select>
                  {errors.projectId && (
                    <small className="text-danger">{errors.projectId.message}</small>
                  )}
                </div>
              </div>

              <hr />
              <h5>Project Estimates Details</h5>
              {/* Tabular UI for multiple item rows */}
              <div className="mb-3 table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Store <span className="text-danger">*</span></th>
                      <th>Location <span className="text-danger">*</span></th>
                      <th>Item Category <span className="text-danger">*</span></th>
                      <th>Item <span className="text-danger">*</span></th>
                      <th>UOM</th>
                      <th>Est Qty <span className="text-danger">*</span></th>
                      <th>Dashboard</th>
                      <th>Stockout Check</th>
                      {!editMode && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {estimateItems.map((row, index) => {
                      const rowLocations = locationsMap[row.storeId] || [];
                      const rowItems = projectItemsList
                        .filter(item => String(item.itemCategoryId) === String(row.itemCategoryId))
                        .map(x => ({
                          id: x.itemId,
                          itemCode: x.itemCode,
                          itemDescription: x.itemDescription,
                          uomName: x.uomName || x.UOM || ""
                        }));

                      return (
                        <tr key={index}>
                          <td>
                            <select
                              className="form-select"
                              disabled={!selectedProjectId}
                              value={row.storeId || ""}
                              onChange={(e) => handleStoreChange(index, e.target.value)}
                            >
                              <option value="">Select Store</option>
                              {stores.map((s) => (
                                <option key={s.storeId || s.id} value={s.storeId || s.id}>
                                  {s.storeName} - {s.storeCode}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="form-select"
                              disabled={!row.storeId}
                              value={row.locationId || ""}
                              onChange={(e) => handleLocationChange(index, e.target.value)}
                            >
                              <option value="">Select Location</option>
                              {rowLocations.map((l) => (
                                <option key={l.locationId || l.id} value={l.locationId || l.id}>
                                  {l.locationName}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="form-select"
                              disabled={!row.locationId}
                              value={row.itemCategoryId || ""}
                              onChange={(e) => handleCategoryChange(index, e.target.value)}
                            >
                              <option value="">Select Category</option>
                              {categories.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="form-select"
                              disabled={!row.itemCategoryId}
                              value={row.itemId || ""}
                              onChange={(e) => handleItemChange(index, e.target.value)}
                            >
                              <option value="">Select Item</option>
                              {rowItems.map((i) => (
                                <option key={i.id} value={i.id}>
                                  {i.itemCode} - {i.itemDescription}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control bg-light"
                              readOnly
                              placeholder="UOM"
                              value={row.UOM || ""}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="any"
                              className="form-control"
                              value={row.estimatedQuantity || ""}
                              onChange={(e) => handleQuantityChange(index, e.target.value)}
                            />
                          </td>
                          <td>
                            <div className="form-check form-switch mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`displayOnDashboard_${index}`}
                                checked={row.displayOnDashboard || false}
                                onChange={(e) => handleDashboardChange(index, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`displayOnDashboard_${index}`}>
                                Dashboard
                              </label>
                            </div>
                          </td>
                          <td>
                            <div className="form-check form-switch mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`stockoutCheck_${index}`}
                                checked={row.stockoutCheck || false}
                                onChange={(e) => handleStockoutCheckChange(index, e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor={`stockoutCheck_${index}`}>
                                Stockout
                              </label>
                            </div>
                          </td>
                          {!editMode && (
                            <td>
                              <button
                                type="button"
                                className="btn btn-success btn-sm me-2"
                                onClick={handleAddRow}
                              >
                                +
                              </button>
                              {estimateItems.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDeleteRow(index)}
                                >
                                  -
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Form Buttons */}
              <div className="mt-3">
                <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                  <i className="ti ti-circle-check me-1"></i>
                  {loading ? (editMode ? "Updating..." : "Saving...") : editMode ? "Update" : "Save"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={loading}
                  onClick={handleCancel}
                >
                  <i className="ti ti-cancel me-1"></i>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
